<template>
  <Teleport to="body">
    <Transition name="offline-overlay" appear>
      <div
        class="offline-sync-overlay"
        role="status"
        aria-live="assertive"
        aria-busy="true"
      >
        <div class="offline-sync-content">
          <img
            v-if="fgSistema" 
            :src="retornaLogo(fgSistema)" 
            class="offline-sync-logo"
          />
          <img
            v-else 
            src="/assets/custom/logos/icone-clinic-blue.png" 
            class="offline-sync-logo"
          />

          <div class="offline-sync-message">
            {{ message }}
          </div>

          <div
            class="wave-loader"
            aria-hidden="true"
          >
            <span 
              v-for="index in 7"
              :key="index"
              :style="{
                animationDelay: `${(index - 1) * 0.1}s`
              }"
            />
          </div>

          <div
            v-if="secondaryMessage"
            class="offline-sync-secondary-message"
          >
            {{ secondaryMessage }}
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "OfflineSyncOverlay",

  props: {
    message: {
      type: String,
      default: "Carregando"
    },

    secondaryMessage: {
      type: String,
      default: "Por favor aguarde..."
    }
  },

  data() {
    return {
      previousBodyOverflow: null
    };
  },

  mounted() {
    this.lockBody();
  },

  beforeUnmount() {
    this.unlockBody();
  },

  computed: {
    ...mapGetters([
      "appAguardandoInternet", 
      "fgSistema"
    ])
  }, 

  methods: {
    retornaLogo(sistema) {
      let logo = "";

      switch (sistema) {
        case "CLINIC":
          logo = "/assets/custom/logos/icone-clinic-blue.png";
          break;
        case "CASH":
          logo = "/assets/custom/logos/icone-cash-blue.png";
          break;
        case "BOT":
          logo = "/assets/custom/logos/icone-clinic-blue.png";
          break;
        case "ANALYTICS":
          logo = "/assets/custom/logos/icone-clinic-blue.png";
          break;
        case "QUALITY":
          logo = "/assets/custom/logos/icone-clinic-blue.png";
          break;
        default:
          logo = "/assets/custom/logos/icone-clinic-blue.png";
          break;
      }

      return logo;
    }, 
    lockBody() {
      if (typeof document === "undefined") {
        return;
      }

      if (this.previousBodyOverflow === null) {
        this.previousBodyOverflow =
          document.body.style.overflow || "";
      }

      document.body.style.overflow = "hidden";
    },

    unlockBody() {
      if (
        typeof document === "undefined" ||
        this.previousBodyOverflow === null
      ) {
        return;
      }

      document.body.style.overflow =
        this.previousBodyOverflow;

      this.previousBodyOverflow = null;
    }
  }
};
</script>

<style scoped>
.offline-sync-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100vw;
  height: 100vh;

  padding: 30px;

  background:
    radial-gradient(
      circle at center,
      rgba(255, 255, 255, 1) 0%,
      rgba(247, 248, 252, 1) 100%
    );

  overflow: hidden;
}

.offline-sync-content {
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  max-width: 620px;

  text-align: center;
}

.offline-sync-logo {
  display: block;

  width: auto;
  max-width: min(280px, 70vw);
  max-height: 110px;

  margin-bottom: 42px;

  object-fit: contain;
}

.offline-sync-message {
  max-width: 600px;

  color: #181c32;

  font-size: 22px;
  font-weight: 600;
  line-height: 1.45;
}

.offline-sync-secondary-message {
  max-width: 520px;

  margin-top: 25px;

  color: #7e8299;

  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
}

.wave-loader {
  display: flex;
  align-items: center;
  justify-content: center;

  height: 56px;

  margin-top: 35px;

  gap: 6px;
}

.wave-loader span {
  display: block;

  width: 6px;
  height: 18px;

  border-radius: 10px;

  background-color: #3699ff;

  transform-origin: center;

  animation: wave-loading 1.1s ease-in-out infinite;
}

@keyframes wave-loading {
  0%,
  100% {
    height: 18px;
    opacity: 0.45;
    transform: scaleY(0.7);
  }

  50% {
    height: 48px;
    opacity: 1;
    transform: scaleY(1);
  }
}

/*
 * Entrada e saída do overlay.
 */
.offline-overlay-enter-active,
.offline-overlay-leave-active {
  transition:
    opacity 350ms ease,
    visibility 350ms ease;
}

.offline-overlay-enter-active .offline-sync-content,
.offline-overlay-leave-active .offline-sync-content {
  transition:
    opacity 350ms ease,
    transform 350ms ease;
}

.offline-overlay-enter-from,
.offline-overlay-leave-to {
  opacity: 0;
  visibility: hidden;
}

.offline-overlay-enter-from .offline-sync-content {
  opacity: 0;
  transform: translateY(12px) scale(0.985);
}

.offline-overlay-leave-to .offline-sync-content {
  opacity: 0;
  transform: translateY(-8px) scale(0.99);
}

@media (max-width: 576px) {
  .offline-sync-overlay {
    padding: 24px;
  }

  .offline-sync-logo {
    max-width: 220px;
    max-height: 90px;
    margin-bottom: 32px;
  }

  .offline-sync-message {
    font-size: 18px;
  }

  .offline-sync-secondary-message {
    font-size: 13px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .offline-overlay-enter-active,
  .offline-overlay-leave-active,
  .offline-overlay-enter-active .offline-sync-content,
  .offline-overlay-leave-active .offline-sync-content {
    transition-duration: 1ms;
  }

  .wave-loader span {
    animation-duration: 1.8s;
  }
}
</style>